import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MailCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { MobileOtpForm } from "@/components/MobileOtpForm";
import { GoogleButton } from "@/components/GoogleButton";

const tabCls = (active) =>
  `flex-1 h-10 rounded-md text-sm font-medium transition-colors ${
    active ? "bg-navy text-white" : "text-slate-500 hover:text-navy"
  }`;

export default function Register() {
  const [mode, setMode] = useState("email");
  const [form, setForm] = useState({ name: "", email: "", password: "", category: "professional" });
  const [loading, setLoading] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const [resending, setResending] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const { register, resendVerification } = useAuth();
  const navigate = useNavigate();
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!agreed) return;
    setLoading(true);
    try {
      const res = await register({ ...form, accept_terms: agreed });
      setSentTo(res.email || form.email);
      toast.success("Verification email sent.");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await resendVerification(sentTo);
      toast.success("If that account needs verification, a new link is on its way.");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-[80vh] grid lg:grid-cols-2" data-testid="register-page">
      <div className="hidden lg:flex flex-col justify-between bg-navy grain p-14">
        <Logo light />
        <div>
          <h2 className="font-display text-4xl font-bold text-white leading-tight">Start your systematic financial-market education journey.</h2>
          <p className="mt-4 text-slate-400">Free foundation courses, research insights and educational tools await.</p>
        </div>
        <p className="text-xs text-slate-500">An independent financial-market education & research platform.</p>
      </div>
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><Logo /></div>

          {sentTo ? (
            <div className="text-center" data-testid="register-verify-sent">
              <div className="mx-auto mb-5 h-14 w-14 rounded-full bg-gold/15 flex items-center justify-center">
                <MailCheck className="h-7 w-7 text-gold" />
              </div>
              <h1 className="font-display text-3xl font-bold text-navy">Check your email</h1>
              <p className="mt-3 text-sm text-slate-500">
                We've sent a verification link to <span className="font-semibold text-navy">{sentTo}</span>.
                Click it to activate your account, then log in.
              </p>
              <button onClick={resend} disabled={resending} data-testid="register-resend" className="mt-6 text-sm text-navy font-semibold hover:text-gold disabled:opacity-60">
                {resending ? "Sending…" : "Resend verification email"}
              </button>
              <p className="mt-6 text-sm text-slate-500">
                <Link to="/login" className="text-navy font-medium hover:text-gold" data-testid="go-login">Back to Login</Link>
              </p>
            </div>
          ) : (
            <>
              <h1 className="font-display text-3xl font-bold text-navy">Create Account</h1>
              <p className="mt-2 text-sm text-slate-500">Join KRV Prime in a minute.</p>

              <div className="mt-6"><GoogleButton label="Sign up with Google" /></div>
              <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
                <span className="h-px flex-1 bg-border" /> or <span className="h-px flex-1 bg-border" />
              </div>

              <div className="flex gap-1 rounded-lg bg-slate-100 p-1" data-testid="register-mode-toggle">
                <button onClick={() => setMode("email")} className={tabCls(mode === "email")} data-testid="register-mode-email">Email</button>
                <button onClick={() => setMode("mobile")} className={tabCls(mode === "mobile")} data-testid="register-mode-mobile">Mobile OTP</button>
              </div>

              {mode === "email" ? (
                <form onSubmit={submit} className="mt-6 space-y-4" data-testid="register-form">
                  <input required value={form.name} onChange={set("name")} data-testid="register-name" placeholder="Full name" className="w-full h-12 rounded-md border border-border px-4 text-sm outline-none focus:ring-2 focus:ring-gold" />
                  <input required type="email" value={form.email} onChange={set("email")} data-testid="register-email" placeholder="Email address" className="w-full h-12 rounded-md border border-border px-4 text-sm outline-none focus:ring-2 focus:ring-gold" />
                  <input required type="password" minLength={6} value={form.password} onChange={set("password")} data-testid="register-password" placeholder="Password (min 6 chars)" className="w-full h-12 rounded-md border border-border px-4 text-sm outline-none focus:ring-2 focus:ring-gold" />
                  <select value={form.category} onChange={set("category")} data-testid="register-category" className="w-full h-12 rounded-md border border-border px-4 text-sm outline-none focus:ring-2 focus:ring-gold">
                    <option value="professional">Working Professional</option>
                    <option value="student">Student</option>
                    <option value="trader">Trader / Investor</option>
                  </select>
                  <label className="flex items-start gap-2 text-xs text-slate-500 cursor-pointer">
                    <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} data-testid="register-agree" className="mt-0.5 accent-navy" />
                    <span>I have read and agree to the <Link to="/terms" className="text-navy hover:text-gold underline">Terms & Conditions</Link> and <Link to="/privacy" className="text-navy hover:text-gold underline">Privacy Policy</Link>.</span>
                  </label>
                  <button type="submit" disabled={loading || !agreed} data-testid="register-submit" className="w-full h-12 rounded-md bg-navy text-white font-semibold text-sm hover:bg-navy-light transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
                    {loading ? "Creating…" : "Create Account"}
                  </button>
                </form>
              ) : (
                <div className="mt-6"><MobileOtpForm withName onSuccess={() => navigate("/dashboard")} /></div>
              )}

              <p className="mt-6 text-sm text-slate-500 text-center">
                Already have an account? <Link to="/login" className="text-navy font-medium hover:text-gold" data-testid="go-login">Login</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
